import React, { useState, useCallback } from "react";
import useSubmissions, {
  STATUSES, GAME_IDS, GAME_LABEL, PSA_TIERS, GRADES, tally, stageOf,
} from "./useSubmissions";
import CardFinder, { Art } from "../cards/CardFinder";
import { GAMES } from "../../shared";
import { GameToggle, CardZoom, useUndo, UndoBar } from "../../ui";

/**
 * A log of cards sent to be graded.
 *
 * One row per card, because that is the unit the money is actually decided on
 * — a submission of ten can have four winners and six that lost you the fee,
 * and an average across them hides both.
 */

const usd = (n) =>
  n == null ? "—" : `${n < 0 ? "−" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Money({ value, className = "" }) {
  return <span className={`sb-money ${className}`}>{usd(value)}</span>;
}

function Row({ entry, onChange, onRemove }) {
  const t = tally(entry);
  const [pricing, setPricing] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  /* On request, never automatically. The graded source is metered and the
     answer is only wanted at the moment somebody asks what a card is worth. */
  const look = async () => {
    setPricing(true);
    const { default: priceEntry } = await import("./priceEntry");
    const got = await priceEntry(entry);
    setPricing(false);
    onChange(entry.id, got.error
      ? { valueNote: got.error, valueBad: true }
      // To the penny. An average of graded sales arrives as 4015.6441666666665,
      // and a box you might type over should not be showing that.
      : { value: got.value.toFixed(2), valueNote: got.note, valueBad: false });
  };

  const money = (field, placeholder) => (
    <input
      className="sb-in sb-in--num"
      value={entry[field] ?? ""}
      onChange={(e) => onChange(entry.id, { [field]: e.target.value.replace(/[^\d.]/g, "") })}
      placeholder={placeholder}
      inputMode="decimal"
      aria-label={`${placeholder} for ${entry.name || "this card"}`}
    />
  );

  return (
    <div className="sb-row">
      {/* Small on purpose. A log is read as a list of numbers, and the picture
          is here to confirm the row is the card you meant — not to be looked
          at. Rows added before this existed, or typed by hand, simply have no
          art and leave the space empty rather than showing a placeholder. */}
      <span className="sb-card">
        {/* Clickable only when there is something to enlarge. A row typed in
            by hand has no art, and a button that opens an empty box is worse
            than no button. */}
        {entry.image ? (
          <button
            type="button"
            className="sb-art-btn"
            onClick={() => setZoomed(true)}
            title="See it bigger"
            aria-label={`Enlarge ${entry.name || "this card"}`}
          >
            <Art src={entry.image} className="sb-art" />
          </button>
        ) : (
          <Art src={entry.image} className="sb-art" />
        )}
        <input
          className="sb-in sb-in--name"
          value={entry.name}
          onChange={(e) => onChange(entry.id, { name: e.target.value })}
          placeholder="Card"
          aria-label="Card name"
        />
      </span>

      {zoomed && (
        <CardZoom
          card={{
            name: entry.name,
            imageUrl: entry.image,
            set: entry.set,
            number: entry.number,
          }}
          onClose={() => setZoomed(false)}
        />
      )}
      <span className="sb-where">
        <input
          className="sb-in sb-in--set"
          value={entry.set}
          onChange={(e) => onChange(entry.id, { set: e.target.value })}
          placeholder="Set"
          aria-label="Set"
        />
        {/* Its own box rather than crammed into the set, because pricing reads
            it: a number typed into the wrong field is a card that will not
            price and no way to see why. */}
        <input
          className="sb-in sb-in--num2"
          value={entry.number ?? ""}
          onChange={(e) => onChange(entry.id, { number: e.target.value })}
          placeholder="No."
          aria-label="Card number"
        />
      </span>

      {/* The only part of the stage that is not already written down further
          along the row. A grade means it is back; a sale means it is sold. */}
      <label className="sb-in sb-sent" title="Has it gone to the grader?">
        <input
          type="checkbox"
          checked={Boolean(entry.sent)}
          onChange={(e) => onChange(entry.id, { sent: e.target.checked })}
          aria-label={`Sent ${entry.name || "this card"} to the grader`}
        />
        <span>{STATUSES.find((s) => s.id === stageOf(entry))?.label}</span>
      </label>

      <select
        className="sb-in sb-in--grade"
        value={entry.grade ?? ""}
        onChange={(e) => onChange(entry.id, { grade: e.target.value })}
        aria-label={`Grade it came back as, for ${entry.name || "this card"}`}
      >
        <option value="">Grade</option>
        {GRADES.map((g) => (
          <option key={g} value={g}>{g}</option>
        ))}
      </select>

      {money("paid", "Paid")}

      {/* Picked from PSA's own tiers rather than remembered, and still a plain
          box underneath so a bulk rate or a shared postage split can be typed. */}
      <div className="sb-grading">
        {money("grading", "Grading")}
        <select
          className="sb-tier"
          value=""
          onChange={(e) => {
            const t = PSA_TIERS.find((x) => x.id === e.target.value);
            if (t) onChange(entry.id, { grading: String(t.price) });
          }}
          aria-label={`PSA tier for ${entry.name || "this card"}`}
          title="Fill from a PSA tier"
        >
          <option value="">▾ PSA tier</option>
          {PSA_TIERS.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label} — ${t.price}
            </option>
          ))}
        </select>
      </div>

      {/* Upcharges, postage split across the submission, tax on the fee. One
          box because they are all the same thing — money spent getting this
          card graded — and the note on the second line says which. */}
      <input
        className="sb-in sb-in--num"
        value={entry.other ?? ""}
        onChange={(e) => onChange(entry.id, { other: e.target.value.replace(/[^\d.]/g, "") })}
        placeholder="Other"
        inputMode="decimal"
        title="Upcharge, your share of postage, tax — anything else this card cost"
        aria-label={`Other costs for ${entry.name || "this card"}`}
      />

      {/* What it is worth, and the button that fills it in. Its own column,
          two along from the sale, because one is an estimate and the other is
          a receipt and they must never be mistaken for each other. */}
      <div className="sb-worth">
        {money("value", "Worth")}
        <button
          type="button"
          className="sb-look"
          onClick={look}
          disabled={pricing}
          title="Look up what this is worth now"
          aria-label={`Look up the price of ${entry.name || "this card"}`}
        >
          {pricing ? "…" : "↻"}
        </button>
      </div>

      {money("sold", "Sold for")}
      {/* Nothing to do with grading. This is the cut eBay or TCGplayer takes
          out of the sale, which is why it is a percentage and why it sits with
          the sale rather than with the costs. */}
      <input
        className="sb-in sb-in--fee"
        value={entry.feePct ?? ""}
        onChange={(e) => onChange(entry.id, { feePct: e.target.value.replace(/[^\d.]/g, "") })}
        placeholder="Sale fee %"
        inputMode="decimal"
        title="What the marketplace takes out of the sale — eBay about 13%, TCGplayer about 12.6%"
        aria-label={`Marketplace fee percent on the sale of ${entry.name || "this card"}`}
      />

      {/* The real one until it has sold, and then only the real one. Before
          that, the estimate — marked as one, so the column is never read as a
          receipt when it is a guess. */}
      {t.profit != null ? (
        <Money value={t.profit} className={t.profit >= 0 ? "is-up" : "is-down"} />
      ) : t.estimate ? (
        <span className="sb-money sb-money--est" title="If you sold at today's price">
          ≈ {usd(t.estimate.profit)}
        </span>
      ) : (
        <Money value={null} className="is-open" />
      )}

      <button
        type="button"
        className="sb-x"
        onClick={() => onRemove(entry.id)}
        aria-label={`Remove ${entry.name || "this card"}`}
      >
        ×
      </button>

      {/* The second line, where there is room. The game does not belong in the
          scrolling columns — it is read once when the row is made and almost
          never again. */}
      <div className="sb-second">
        <select
          className="sb-in sb-in--game"
          value={entry.game || "pokemon"}
          onChange={(e) => onChange(entry.id, { game: e.target.value })}
          aria-label={`Game for ${entry.name || "this card"}`}
        >
          {GAME_IDS.map((g) => (
            <option key={g} value={g}>{GAME_LABEL[g]}</option>
          ))}
        </select>
        <input
          className="sb-in sb-in--note"
          value={entry.note}
          onChange={(e) => onChange(entry.id, { note: e.target.value })}
          placeholder="Notes — grader, submission number, what you saw in it"
          aria-label="Notes"
        />
      </div>

      {/* What the price was actually based on. A figure with no provenance is
          one you have to re-derive every time you look at it. */}
      {entry.valueNote && (
        <p className={`sb-note${entry.valueBad ? " is-bad" : ""}`}>{entry.valueNote}</p>
      )}
    </div>
  );
}

export default function Submissions({ game = "pokemon" }) {
  const { entries, add, update, remove, restore, totals } = useSubmissions();
  const [filter, setFilter] = useState("all");

  /* A submission row is a record of money spent and money made. Deleting one
     by mistake used to be gone for good; now it is gone for six seconds. */
  const undo = useUndo();
  const removeWithUndo = useCallback((id) => {
    const taken = remove(id);
    if (taken) undo.offer(taken.entry.name || "submission", () => restore(taken.entry, taken.index));
  }, [remove, restore, undo]);

  /* Which catalogue the search looks in, and it has to be asked here.
     This screen holds both games at once, which is exactly why the top bar
     hides its game toggle on it — and that left the search stuck in whatever
     game the app was last in, with no way to change it without leaving. A log
     that covers both games needs a search that does too. */
  const [findGame, setFindGame] = useState(game);
  const findingOp = findGame === GAMES.ONE_PIECE;

  const shown = filter === "all" ? entries : entries.filter((e) => stageOf(e) === filter);

  const addRow = useCallback(() => {
    // Whichever game the app is in, since that is nearly always the one being
    // logged. The row can still be switched.
    add({ game });
    // The new row goes on top, so there is nothing to scroll to.
  }, [add, game]);

  /**
   * A card picked from the search, as a row.
   *
   * Fills only what the card actually says — name, set, number, art — and
   * leaves every number blank. What you paid is not something the app can
   * know, and a zero sitting in that box would be a figure somebody could
   * miss rather than an empty one they have to fill.
   *
   * One Piece carries its code in `number` and nothing separate to call a
   * set, so the print label is the set line and the code is shown with it.
   */
  const addFound = useCallback(
    (card) => {
      add({
        // The game it was found in, not the one the app is in — those are
        // different questions on a screen that shows both.
        game: findGame,
        name: card.name,
        set: card.set || "",
        /* What is printed on the card, which for a DON!! card is nothing —
           its id exists so the app can store it, and is not a card number. */
        number: card.printed ?? card.number ?? "",
        image: card.image || "",
      });
    },
    [add, findGame]
  );

  /* Only worth splitting once there is something to split. A per-game
     breakdown of a log holding one game is a row of noise saying what the
     total above it already said. */
  const games = GAME_IDS.filter((g) => totals.byGame[g]?.count);
  const splitWorthShowing = games.length > 1;

  return (
    <div className="sb">
      <div className="sb-head">
        <h2 className="section-title">Submissions</h2>
        <p className="sb-sub">
          What you paid, what grading cost, what it came back as, and what it sold
          for. Press <strong>↻</strong> to fetch what a card is worth today — the PSA
          comp for the grade you entered, or the raw price.
        </p>
        {/* Asked once already, so it is answered on the screen rather than
            left to be worked out from two similar-looking boxes. */}
        <p className="sb-sub sb-sub--dim">
          <strong>Grading</strong> is what PSA charges you — pick a tier or type it.
          <strong> Sale fee %</strong> is the cut eBay or TCGplayer takes out of the
          sale, so it only applies once something sells. Tier prices are a snapshot;
          check PSA before you rely on them.
        </p>
      </div>

      {/* Sold and unsold apart, on purpose. Rolled together, profit improves
          every time you spend money — because cards still with the grader have
          costs and no revenue yet. */}
      <div className="sb-totals">
        <div className="sb-tot">
          <span className="sb-tot-label">Spent</span>
          <Money value={totals.spent} />
          <span className="sb-tot-note">{totals.count} card{totals.count === 1 ? "" : "s"}</span>
        </div>
        <div className="sb-tot">
          <span className="sb-tot-label">Back from sales</span>
          <Money value={totals.revenue} />
          <span className="sb-tot-note">after fees · {totals.soldCount} sold</span>
        </div>
        <div className="sb-tot">
          <span className="sb-tot-label">Still out</span>
          <Money value={totals.outstanding} />
          <span className="sb-tot-note">{totals.openCount} not sold yet</span>
        </div>
        <div className="sb-tot sb-tot--profit">
          <span className="sb-tot-label">Profit on what sold</span>
          <Money
            value={totals.soldCount ? totals.profit : null}
            className={totals.profit >= 0 ? "is-up" : "is-down"}
          />
          <span className="sb-tot-note">
            {totals.soldCount ? "the only figure that has happened" : "nothing sold yet"}
          </span>
        </div>
      </div>

      {/* One Piece has no price source in this app at all, so whether grading
          it pays is a question nothing else here can answer. */}
      {splitWorthShowing && (
        <div className="sb-split">
          {games.map((g) => {
            const b = totals.byGame[g];
            return (
              <div key={g} className="sb-split-row">
                <span className="sb-split-game">{GAME_LABEL[g]}</span>
                <span className="sb-split-note">
                  {b.count} card{b.count === 1 ? "" : "s"} · <Money value={b.spent} /> spent
                  {b.soldCount > 0 && <> · {b.soldCount} sold</>}
                </span>
                <span className="sb-split-profit">
                  {b.soldCount ? (
                    <Money value={b.profit} className={b.profit >= 0 ? "is-up" : "is-down"} />
                  ) : (
                    <span className="sb-money is-open">nothing sold yet</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Searching first, typing second. The name, set, number and art are all
          already known to the app, and re-typing them by hand is how "Umbrean
          VMAX 251" ends up in a log that later has to be priced. Everything it
          fills is a plain box afterwards, so a card the search has never heard
          of is still just typed in. */}
      <div className="sb-find">
        <div className="sb-find-head">
          <span className="sb-label">Log a card</span>
          <GameToggle game={findGame} onChange={setFindGame} />
        </div>
        <CardFinder
          key={findGame}
          isOnePiece={findingOp}
          onPick={addFound}
          clearOnPick
          placeholder={
            findingOp
              ? "Search a card to log — Luffy, OP13-118…"
              : "Search a card to log — name, number, or set"
          }
        />
      </div>

      <div className="sb-bar">
        <button type="button" className="btn btn-secondary" onClick={addRow}>
          Add a blank row
        </button>
        <div className="sb-filters" role="group" aria-label="Show">
          {[["all", "All"], ...STATUSES.map((s) => [s.id, s.label])].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`sb-chip${filter === id ? " is-on" : ""}`}
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Above whatever is below, including the "nothing logged yet" message:
          deleting the last row is exactly when the offer matters most. */}
      <UndoBar pending={undo.pending} onUndo={undo.undo} verb="Deleted" />

      {entries.length === 0 ? (
        <p className="msg-empty">
          Nothing logged yet. Add a card when you buy it, fill in the grading cost when
          you send it, and the sale when it goes — the profit works itself out.
        </p>
      ) : shown.length === 0 ? (
        <p className="msg-empty">Nothing at that stage right now.</p>
      ) : (
        <div className="sb-table">
          <div className="sb-row sb-row--head">
            <span>Card</span>
            <span>Set / number</span>
            <span>Sent?</span>
            <span>Grade</span>
            <span>Paid</span>
            <span>Grading</span>
            <span>Other</span>
            <span>Worth now</span>
            <span>Sold for</span>
            <span>Sale fee %</span>
            <span>Profit</span>
            <span />
          </div>
          {shown.map((e) => (
            <Row key={e.id} entry={e} onChange={update} onRemove={removeWithUndo} />
          ))}
        </div>
      )}
    </div>
  );
}
